import { useState } from 'react';
import {
  Badge,
  Box,
  Heading,
  Stack,
  Text,
  AccordionItemBody,
  Flex,
  Button,
  Icon,
} from '@chakra-ui/react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { NavLink } from 'react-router-dom';
import PrintButton from '@/components/ui/print-button';
import { LoginButton } from '@/components/ui/login-button';
import {
  AccordionItem,
  AccordionItemContent,
  AccordionItemTrigger,
  AccordionRoot,
} from '@/components/ui/accordion';
import { Prose } from '@/components/ui/prose';
import { toaster } from '@/components/ui/toaster';
import ContentDrawer from '@/pages/contents/content-drawer';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import { baseApi } from '@/utils/api';
import { Collection, Content } from '@/lib/types';

interface Item {
  collection: Collection;
  ContentWithStructure: {
    groups: {
      name: string;
      contents: string[];
      ContentValues: {
        reference_id: string;
        name: string;
        description: string;
        content_type_name: string;
      }[];
    }[];
  };
  User: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface Props {
  item: Item;
  isPublic: boolean;
}

export default function ViewContent({ item, isPublic = false }: Props) {
  const [selectedContent, setSelectedContent] = useState<Content>();
  const contentDrawer = useModalToggle();

  const handleView = async (referenceId: string) => {
    try {
      const response = await baseApi.get(`public/content/${referenceId}`);
      const content = await response.data.data;
      if (!content) {
        return;
      }
      setSelectedContent(content);
      contentDrawer.open();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch content',
      });
    }
  };

  const defaultValues = isPublic
    ? item.ContentWithStructure?.groups.map((_: unknown, index: number) => index + '')
    : ['0'];

  return (
    <Box pb={6} className='print-container'>
      {/* Header */}
      <Flex justifyContent='space-between' alignItems='flex-start' mb={4} mt={2} gap={3}>
        <Box flex={1}>
          <Heading as='h2' size='xl' color='fg.strong' mb={1}>
            {item.collection?.name}
          </Heading>
          {item.collection?.description && (
            <Box color='fg.muted' fontSize='sm'>
              <Prose size='sm'>
                <div dangerouslySetInnerHTML={{ __html: item.collection.description }} />
              </Prose>
            </Box>
          )}
        </Box>
        {isPublic ? (
          <Flex gap={3} flexShrink={0}>
            <PrintButton documentTitle={item.collection?.name} variant='outline' />
            <LoginButton />
          </Flex>
        ) : (
          <Button variant='secondary' size='sm' asChild flexShrink={0} mr={6}>
            <NavLink to={`/public/collections/${item.collection?.reference_id}`} target='_blank'>
              Print
              <Icon as={PrinterIcon} w={4} h={4} />
            </NavLink>
          </Button>
        )}
      </Flex>

      {/* Sections accordion */}
      <Stack gap={3}>
        <AccordionRoot defaultValue={defaultValues} multiple>
          {item.ContentWithStructure?.groups.map((group, index: number) => (
            <Box
              key={index}
              bg='bg.panel'
              border='1px solid'
              borderColor='border'
              borderRadius='lg'
              overflow='hidden'
            >
              <AccordionItem value={index + ''}>
                <AccordionItemTrigger px={4} py={3}>
                  <Box as='span' flex={1}>
                    <Text fontSize='sm' fontWeight='semibold' color='fg.strong'>
                      {group.name}
                    </Text>
                  </Box>
                  {group.ContentValues && (
                    <Badge bg='bg.subtle' color='fg.muted' size='sm' mr={2}>
                      {group.ContentValues.length}
                    </Badge>
                  )}
                </AccordionItemTrigger>
                <AccordionItemContent>
                  <AccordionItemBody px={3} pb={3}>
                    {group.ContentValues && group.ContentValues.length > 0 ? (
                      <Stack gap={2}>
                        {group.ContentValues.map((content) => (
                          <Box
                            key={content.reference_id}
                            py={2}
                            px={3}
                            bg='bg.panel'
                            border='1px solid'
                            borderColor='border'
                            borderRadius='md'
                            transition='box-shadow 0.15s ease, border-color 0.15s ease'
                            _hover={{
                              cursor: 'pointer',
                              shadow: 'sm',
                              borderColor: 'border.emphasized',
                            }}
                            onClick={() => handleView(content.reference_id)}
                          >
                            <Text fontSize='sm' fontWeight='medium' color='fg.strong'>
                              {content.name}
                            </Text>
                            <Text fontSize='xs' color='fg.muted'>
                              {content.content_type_name}
                            </Text>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Text fontSize='sm' color='fg.muted'>
                        No content available.
                      </Text>
                    )}
                  </AccordionItemBody>
                </AccordionItemContent>
              </AccordionItem>
            </Box>
          ))}
        </AccordionRoot>
      </Stack>

      {selectedContent && <ContentDrawer {...contentDrawer.props} content={selectedContent} />}
    </Box>
  );
}
